/**
 * How THIS app reaches the backend: its session token, its timeout, its error
 * decoding — installed once via `configureUi` and used by every client in `@api`.
 *
 * Transport only. Which route to call, and what the answer means, is `@api`'s; what
 * goes ON the request is this app's, and it is the whole of what differs between a
 * dashboard session and the Helper's 90-day device token.
 *
 * `path` is the REAL backend path — `/v1/communities/{id}/members` — with no prefix
 * added and none implied. `@api/v1/http` is what puts the `/v1` on, in one line.
 */

import { CodedApiError } from '@api/errors';
import type { V1ErrorBody } from '@api/errors';
import type { QueryParams } from '@api/v1/envelope';

import { getToken } from './admin';

// Bracket access matches how the app already reads env (`admin.ts:9`) and keeps
// svelte-check happy under `noPropertyAccessFromIndexSignature`.
const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? 'http://localhost:8000';


/** Same deadline as the legacy client (`admin.ts:314`), for the same reason: a
 *  promise that never settles leaves the loading skeleton up for good. */
const REQUEST_TIMEOUT_MS = 20_000;

// ---------------------------------------------------------------------------
// §1.9 — errors
// ---------------------------------------------------------------------------


async function v1Error(res: Response): Promise<CodedApiError> {
  const fallback = `${res.status} ${res.statusText}`;
  try {
    const body = (await res.json()) as { error?: Partial<V1ErrorBody>; detail?: unknown };
    const e = body.error;
    if (e && typeof e.code === 'string') {
      return new CodedApiError(res.status, e.code, e.message ?? fallback, e.details ?? {});
    }
    // Not the v1 shape. `V1Route` only wraps routes that MATCHED, so an unknown
    // /v1 path, a 405, or anything Starlette answers before routing still comes
    // back as `{"detail": …}`. Read it rather than losing the message.
    if (typeof body.detail === 'string') {
      return new CodedApiError(res.status, 'unknown_error', body.detail);
    }
  } catch {
    /* not JSON — fall through to the status line */
  }
  return new CodedApiError(res.status, 'unknown_error', fallback);
}

// ---------------------------------------------------------------------------
// §1.3 / §1.6 / §1.7 — query parameters
// ---------------------------------------------------------------------------


/** The absolute URL for a v1 path. `null`/`undefined` values are dropped, so an
 *  optional filter can be passed unconditionally. */
/** Query-string builder shared by both URL forms below. */
function withQuery(base: string, path: string, params: QueryParams): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) for (const one of value) qs.append(key, String(one));
    else qs.append(key, String(value));
  }
  const q = qs.toString();
  return q ? `${base}${path}?${q}` : `${base}${path}`;
}

/** The real backend path, base URL applied and nothing implied. */
function apiUrl(path: string, params: QueryParams = {}): string {
  return withQuery(API_BASE, path, params);
}



// ---------------------------------------------------------------------------
// §1.2 — the envelopes
// ---------------------------------------------------------------------------


/** The four sidebar counters, and the one v1 shape that belongs to no single
 *  resource: `GET /v1/me` carries it, and so does every write that moves one of
 *  the counters — `POST …/moderation/items/actions/clear`,
 *  `PATCH …/moderation/items/{kind}/{id}`, `DELETE …/outbox/items/{id}`. It lives
 *  here for the same reason it lives in `api/v1/shapes.py` rather than in
 *  `api/v1/principal.py`: three resource modules emit it, so it is part of the
 *  contract, not part of any one of them.
 *
 *  Hand it to `applyBadges` (`lib/todos.svelte.ts`) after a mutation and the
 *  sidebar is up to date with no refetch at all (doc 04 §4.2, W2/W7). */
// One definition, in the package that types the routes returning it.
export type { Badges } from '@api';


// ---------------------------------------------------------------------------
// The wrapper
// ---------------------------------------------------------------------------

async function request<T>(
  method: string,
  path: string,
  params: QueryParams,
  body: unknown,
): Promise<T> {
  const token = getToken();
  const hasBody = body !== undefined;
  let res: Response;
  try {
    res = await fetch(apiUrl(path, params), {
      method,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(hasBody ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      // Same sentence the legacy client shows, so a dead socket reads the same
      // whichever surface the page is on.
      throw new CodedApiError(0, 'timeout', 'The server didn’t respond — check your connection and retry.');
    }
    throw err;
  }
  if (!res.ok) throw await v1Error(res);
  // v1 never answers 204 (§1.2: a mutation returns the resource), but a proxy or a
  // future route could; returning undefined beats throwing inside `res.json()`.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}


/**
 * GET any backend path, with this app's base URL, auth, timeout and errors.
 *
 * `path` is the REAL path — `/v1/communities/{id}/members/{id}` — not one relative to
 * `/v1`. That difference is the whole reason this exists: `apiGet` takes a v1-relative
 * path, which is fine inside this app and a trap for code shared with the extension,
 * where the other client has no such prefix to imply. Shared code writes the path it
 * would read in the API docs, and each app supplies this.
 */
export function apiGet<T>(path: string, params: QueryParams = {}): Promise<T> {
  return request<T>('GET', path, params, undefined);
}

/**
 * One authenticated WRITE against the REAL backend path, for shared code.
 *
 * The verb is a parameter because `apiPost`/`apiPatch`/`v1Put` below differ in nothing
 * else, and the shared package needs one seam rather than four.
 */
export function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  params: QueryParams = {},
): Promise<T> {
  return request<T>(method, path, params, body);
}

/**
 * The same request, uninterpreted: this app's base URL and session token, and the
 * `Response` handed straight back.
 *
 * For the one call no JSON client can express — the suggestion stream, which POSTs
 * and reads SSE frames. It does NOT throw on a non-2xx and has NO timeout: a stream
 * is long-lived by design, and `AbortSignal.timeout` would cut a slow draft off
 * mid-sentence. The caller passes its own signal if it wants one.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  return fetch(apiUrl(path), {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  });
}





// ---------------------------------------------------------------------------
// The stream
// ---------------------------------------------------------------------------
//
// WHY TRANSPORT LIVES HERE AND NOT IN THE RESOURCE MODULE. `request()` above is
// JSON-only: it awaits the whole body, and it arms a 20-second timeout. Both are
// wrong for `POST …/moderation/suggestions`, the one v1 route that answers
// `text/event-stream` — the body is consumed incrementally and a three-stage LLM
// pipeline routinely takes longer than the deadline a JSON request should have.
// The alternative was what `lib/admin.ts:1799` does today: a hand-rolled `fetch` in
// a page module that re-implements the auth header, the base URL and the error
// mapping, and drifts from them. Streaming is a transport concern; this file is the
// transport, so the SSE reader is here and `@api/v1/moderation` builds the typed
// wrapper on top of it.

/*
 * `v1PostStream` and `SseFrameHandler` were here — the SSE reader for the suggestion
 * stream, the only streaming route either app calls. It moved to
 * `@ui/composer/suggest` with its one caller, where it replaced a second, identical
 * reader that the Helper's client was carrying. `apiFetch` above is what it uses:
 * this app's base URL and session token, and the `Response` handed straight back.
 */
